import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import useApi from '../hooks/useApi';
import AllBooksPage from '../components/booksList';
import MyBooksPage from '../components/myBooksList';
import UploadPage from '../components/uploadPdf';
import SignUpPage from '../components/signUp';
import LoginPage from '../components/login';
import ForgotPasswordPage from '../components/forgotPassword';
import NavBar from '../components/navBar';
import ReportModal from '../components/reportModal';
import TermsModal from '../components/termsModal';
import PrivacyModal from '../components/privacyModal';
import UploadThemePage from '../components/uploadThemePage';
import AboutUsModal from '../components/aboutUsModal';
import CancellationRefundModal from '../components/refundModal';
import HomepageBlogsTicker from '../components/homepageBlogsTicker';
import PageADaySection from '../components/pageADaySection';
import NativeAffiliateBanner from '../components/ads/NativeAffiliateBanner';
import posthog from 'posthog-js';
import usePageSeo from '../utils/usePageSeo';
import { loadHomepageBooks } from '../utils/homepageBooks';

const URL_SYNCED_VIEWS = new Set([
  'books',
  'my_books',
  'upload',
  'upload_theme',
  'login',
  'signup',
  'forgot_password',
  'dashboard',
]);

function viewToSearch(view) {
  if (!view || view === 'home' || view === 'dashboard') return '';
  return `?view=${encodeURIComponent(view)}`;
}

export default function HomePage() {

  usePageSeo({
    title: 'Read, Upload & Write Books Online',
    description: 'Read public domain books, convert PDFs to themed ebooks, and write your own books on Poppyruz.',
    path: '/',
  });

  const { request } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quotesRef = useRef(null);
  const publicBooksRef = useRef(null);
  const myBooksRef = useRef(null);
  const scrollLeft = (ref) => ref.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = (ref) => ref.current?.scrollBy({ left: 300, behavior: 'smooth' });
  const [publicBooks, setPublicBooks] = useState([]);
  const [myBooks, setMyBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMyBooksLoading, setIsMyBooksLoading] = useState(true);
  const [showAllBooks, setShowAllBooks] = useState(false);
  const [showMyBooks, setShowMyBooks] = useState(false);
  const [showUploadPdf, setShowUploadPdf] = useState(false);
  const [showUploadTheme, setShowUploadTheme] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const applyViewState = useCallback((view) => {
    setShowAllBooks(view === 'books');
    setShowUploadPdf(view === 'upload');
    setShowLogin(view === 'login');
    setShowSignUp(view === 'signup');
    setShowForgotPassword(view === 'forgot_password');
    setShowDashboard(view === 'dashboard');
    setShowMyBooks(view === 'my_books');
    setShowUploadTheme(view === 'upload_theme');
  }, []);

  const navigateToView = useCallback(
    (view, { replace = false } = {}) => {
      const v = view || 'home';
      applyViewState(v);
      posthog.capture(`navigate_${v}_view`);
      navigate({ pathname: '/', search: viewToSearch(v) }, { replace });
    },
    [applyViewState, navigate],
  );

  const handleNavigate = navigateToView;

  const authenticated = () => {
    setIsAuthenticated(true);
    myBooksApi();
    posthog.capture('login_success');
    navigateToView('dashboard', { replace: true });
  };

  const updateShowLogin = () => {
    navigateToView('login');
    posthog.capture('login_button_clicked');
  };

  const onShowSignUp = () => {
    navigateToView('signup');
    posthog.capture('signup_button_clicked');
  };

  const onShowForgotPassword = () => {
    navigateToView('forgot_password');
  };

  function isTokenValid(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch (e) {
      return false;
    }
  }

  const logoutApi = async () => {
    try {
      await request({
        method: 'post',
        url: `/api/v1/users/logout/`,
        withAuth: true,
        data: {
          refresh: localStorage.getItem('refresh_token'),
        },
      });
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const handleLogout = useCallback(() => {
    void logoutApi();
    localStorage.clear();
    setIsAuthenticated(false);
    navigateToView('home', { replace: true });
    posthog.capture('logout_clicked');
  }, [navigateToView, request]);

  const myBooksApi = async () => {
    setIsMyBooksLoading(true);
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/my_books/`,
        withAuth:true,
      });
      if(data){
        setMyBooks(data.books)
      }
    } catch (err) {
      console.error('API Error:', err);
    }finally {
      setIsMyBooksLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadBooks = async () => {
      setIsLoading(true);
      try {
        const data = await loadHomepageBooks({ limit: 24 });
        if (cancelled) return;
        setPublicBooks(data.books || []);
      } catch (err) {
        console.error('API Error:', err);
        if (!cancelled) {
          setPublicBooks([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    loadBooks();
    const token = localStorage.getItem('access_token');
    const refresh = localStorage.getItem('refresh_token');
    const verifyOrRefresh = async () =>  {
      if (token && isTokenValid(token)) {
        setIsAuthenticated(true);
      } else if (refresh) {
        try {
          const res = await request({
            method: 'post',
            url: '/api/v1/users/refresh/',
            data: {
              refresh: refresh,
            },
          });
          if (res.data.access) {
            localStorage.setItem('access_token', res.data.access);
            localStorage.setItem('refresh_token', res.data.refresh);
            setIsAuthenticated(true);
          } else {
            throw new Error('No access token returned');
          }
        } catch (err) {
          console.error('Refresh token failed:', err);
          localStorage.clear();
          setIsAuthenticated(false);
        }
      } else {
        localStorage.clear();
        setIsAuthenticated(false);
      }
    }
    if(token !== null && refresh !== null && isAuthenticated == false){
      verifyOrRefresh();
    }
    if(token !== null){
      myBooksApi();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const param = searchParams.get('view');
    if (param && URL_SYNCED_VIEWS.has(param)) {
      applyViewState(param);
      return;
    }
    if (!param) {
      applyViewState(isAuthenticated ? 'dashboard' : 'home');
    }
  }, [searchParams, isAuthenticated, applyViewState]);

  useEffect(() => {
    const handleSessionExpired = () => {
      alert('Your session has expired. Please log in again.');
      setIsAuthenticated(false);
      navigateToView('login', { replace: true });
    };
    window.addEventListener("session_expired", handleSessionExpired);
    return () => window.removeEventListener('session_expired', handleSessionExpired);
  }, [navigateToView]);

  if (showDashboard){
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'dashboard'}
          onNavigate={handleNavigate}
        />

        {isMyBooksLoading ? (
          // Global loading spinner
          <div className="flex justify-center items-center py-32">
            <div className="w-10 h-10 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : myBooks.length > 0 ? (
          <section className="relative px-6 py-32 max-w-full">
            <h2 className="text-3xl font-bold mb-8 text-center">Continue reading</h2>
            <div className="relative">
              {/* Arrows */}
              {myBooks.length > 7 && (
                <>
                  <button
                    onClick={() => scrollLeft(myBooksRef)}
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
                  >
                    <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
                  </button>
                  <button
                    onClick={() => scrollRight(myBooksRef)}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
                  >
                    <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                  </button>
                </>
              )}
              {/* Scrollable Container */}
              <div
                  ref={myBooksRef}
                  className="flex flex-nowrap overflow-x-auto snap-x snap-mandatory scroll-smooth gap-6 px-8"
                >
                {myBooks.map((book, index) => (
                  <div
                    key={book.book_meta_unique_id}
                    className="flex-none shrink-0 w-40 snap-start flex flex-col items-center cursor-pointer group"
                    onClick={() => {
                      posthog.capture('clicked_my_book', {
                        book_id: book.book_meta_unique_id,
                        book_name: book.book_name,
                        progress: book.progress
                      });
                      window.location.href =
                        book.is_authored === 1 || book.is_authored === true
                          ? `/read_authored/${book.book_meta_unique_id}`
                          : `/read_book/${book.book_meta_unique_id}`;
                    }}
                  >
                    <img
                      src={book.book_cover}
                      alt={book.book_name}
                      className="w-40 h-56 object-cover rounded-md shadow-md transition duration-300 transform group-hover:scale-105 group-hover:shadow-xl"
                    />
                    <p className="mt-3 text-sm font-medium text-gray-700 text-center group-hover:text-violet-900 transition line-clamp-2 h-10">
                      {book.book_name}
                    </p>
                    {/* Progress Bar */}
                    <div className="w-full mt-2 h-2 bg-gray-300 rounded overflow-hidden">
                      <div
                        className="h-full bg-violet-800 transition-all"
                        style={{ width: book.progress >= book.snapshot_progress ? `${book.progress}%` : `${book.snapshot_progress}%`}}
                      ></div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 text-center">
                      {Math.round(book.progress >= book.snapshot_progress ? book.progress:book.snapshot_progress)}% read
                    </p>
                  </div>
                ))}
              </div>

              {/* See All My Books Link */}
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => handleNavigate('my_books')}
                  className="inline-block text-violet-900 font-medium hover:underline transition"
                >
                  See all your books →
                </button>
              </div>
            </div>
          </section>
        ) : (
          <section className="relative w-full py-32 text-center text-gray-800 mb-5 overflow-hidden">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-center bg-cover opacity-20"
              style={{ backgroundImage: "url('/images/medievel_harbour.jpg')" }}
            />

            {/* Foreground Content */}
            <div className="relative z-10 max-w-2xl mx-auto px-6">
              <h2 className="text-4xl font-bold mb-4">Get Started</h2>
              <p className="text-lg mb-6">
                You haven't started reading yet. Select a public domain book or upload your own to begin.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button
                  type="button"
                  onClick={() => handleNavigate('books')}
                  className="bg-violet-900 text-white px-6 py-3 rounded hover:bg-violet-950 transition"
                >
                  Explore Public Books
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate('upload')}
                  className="bg-orange-600 text-white px-6 py-3 rounded hover:bg-orange-700 transition"
                >
                  Upload a PDF
                </button>
              </div>
            </div>
          </section>
        )}

        <PageADaySection />

        {/* Public Domain Books Section */}
        <section className="relative px-6 py-20 max-w-full">
          <h2 className="text-3xl font-bold mb-8 text-center">Public Domain Books</h2>
          <div className="relative">
            {/* Arrows */}
            <button
              onClick={() => scrollLeft(publicBooksRef)}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
            </button>
            <button
              onClick={() => scrollRight(publicBooksRef)}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
            >
              <ChevronRightIcon className="h-6 w-6 text-gray-600" />
            </button>

            {/* Scrollable Container */}
            {isLoading ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <div className="w-8 h-8 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : publicBooks.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <img src="/images/empty-bookshelf.svg" alt="No books" className="mx-auto h-40 mb-4" />
                <p className="text-lg font-medium">No public domain books available right now.</p>
                <p className="text-sm mt-2">Please check back later — we're adding more classics soon.</p>
              </div>
            ) : (
              <div
                ref={publicBooksRef}
                className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth gap-6 px-8"
                >
                {Array.from({ length: Math.ceil(publicBooks.length / 2) }).map((_, colIndex) => (
                  <div
                      key={colIndex}
                      className="flex-none shrink-0 snap-start flex flex-col gap-6"
                    >
                    {publicBooks.slice(colIndex * 2, colIndex * 2 + 2).map((book, index) => (
                      <div
                        key={book.book_meta_unique_id}
                        className="flex flex-col items-center w-40 cursor-pointer group"
                        onClick={() => {
                          posthog.capture('clicked_public_book', {
                            book_id: book.book_meta_unique_id,
                            book_name: book.book_name
                          });
                          window.location.href =
                        book.is_authored === 1 || book.is_authored === true
                          ? `/read_authored/${book.book_meta_unique_id}`
                          : `/read_book/${book.book_meta_unique_id}`;
                        }}
                        >
                        <img
                          src={book.book_cover}
                          alt={book.book_name}
                          className="w-40 h-56 object-cover rounded-md shadow-md transition duration-300 transform group-hover:scale-105 group-hover:shadow-xl"
                        />
                        <p
                          className="mt-3 text-sm font-medium text-gray-700 text-center group-hover:text-violet-900 transition line-clamp-2 h-10"
                        >
                          {book.book_name}
                        </p>
                    </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {/* See All Books Link */}
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => handleNavigate('books')}
                className="inline-block text-violet-900 font-medium hover:underline transition"
              >
                See all books →
              </button>
            </div>
          </div>
        </section>
        <HomepageBlogsTicker />
        <NativeAffiliateBanner placement="dashboard" />
        <footer className="bg-gray-100 text-gray-700 px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-left">
          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Quick Links</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    handleNavigate('books');
                    posthog.capture('footer_clicked_public_books');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Public Domain Books
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/blogs');
                    posthog.capture('footer_clicked_blogs');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Blogs
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = `/contact`;
                    posthog.capture('footer_clicked_contact');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setShowAboutModal(true);
                  }}
                  className="hover:underline text-left text-sm"
                >
                  About Us
                </button>
              </li>
            </ul>
          </div>
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Contact</h3>
            <p className="text-sm">📧 support@poppyruz.com</p>
            <p className="text-sm">📍 Tirunelveli, India</p>
          </div>
          {/* Feedback & Legal */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Support & Legal</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  onClick={() => {
                    window.location.href = `/contact`;
                    posthog.capture('footer_clicked_feedback');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Give Feedback
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowReportModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Report Copyright Violation
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Terms & Conditions
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowPrivacyModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Privacy Policy
                </button>
              </li>
            </ul>
          </div>
          {/* Social Media */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Follow Us</h3>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/zindha_madhar?igsh=MXZid3QzdGd4bnQ2Ng=="
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/instagram.svg" alt="Instagram" className="h-5 w-5" /></a>
              <a href="https://www.linkedin.com/in/mohamed-sindha-madhar-a9525822a"
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/linkedin.svg" alt="LinkedIn" className="h-5 w-5" /></a>
              <a href="https://www.youtube.com/@Poppyruz"
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/youtube.svg" alt="YouTube" className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center text-sm text-gray-400">
          © 2026 Poppyruz. All rights reserved.
        </div>
      </footer>
      <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
        />
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <AboutUsModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} /> 
      </div>
    );
  }

  if (showSignUp) {
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
        <div className="pt-20">
          <SignUpPage updateShowLogin = {updateShowLogin} authenticated = {authenticated}/>
        </div>
      </div>
    );
  }

  if (showLogin) {
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'login'}
          onNavigate={handleNavigate}
        />
        {/* Upload Component with padding for nav */}
        <div className="pt-20">
          <LoginPage onShowSignUp={onShowSignUp} onShowForgotPassword={onShowForgotPassword} authenticated={authenticated}/>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'login'}
          onNavigate={handleNavigate}
        />
        <div className="pt-20">
          <ForgotPasswordPage onShowLogin={updateShowLogin} />
        </div>
      </div>
    );
  }

  if (showUploadPdf) {
    return (
      <div>
        {/* NavBar */}
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'upload'}
          onNavigate={handleNavigate}
        />
        {/* Upload Component with padding for nav */}
        <div className="pt-20">
          <UploadPage updateShowLogin = {updateShowLogin} onShowSignUp = {onShowSignUp}/>
        </div>
      </div>
    );
  }

  if (showUploadTheme) {
    return (
      <div>
        {/* NavBar */}
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'upload'}
          onNavigate={handleNavigate}
        />
        {/* Upload Component with padding for nav */}
        <div className="pt-20">
          <UploadThemePage updateShowLogin = {updateShowLogin} onShowSignUp = {onShowSignUp}/>
        </div>
      </div>
    );
  }

  if (showMyBooks) {
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'my_books'}
          onNavigate={handleNavigate}
        />
        <div className="pt-20">
          <MyBooksPage books={myBooks} isLoading={isMyBooksLoading}/>
        </div>
      </div>
    );
  }

  if (showAllBooks) {
    return (
      <div>
        <NavBar
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          currentView={'books'}
          onNavigate={handleNavigate}
        />
        <div className="pt-20">
          <AllBooksPage isLoading={isLoading} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-gray-900">
      <NavBar
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        currentView={'home'}
        onNavigate={handleNavigate}
      />
      {/* Hero */}
      <section className="text-center px-6 py-20 mt-12 bg-gradient-to-b from-violet-50 to-white">
        <h1 className="text-5xl font-bold mb-6">Read, Upload, Write books.</h1>
        <p className="text-lg text-gray-600 mb-8">Read public domain books, Convert PDFs into books, Read with themes and Become an Author!</p>
        <div>
          <button
            type="button"
            onClick={() => handleNavigate('signup')}
            className="bg-violet-900 text-white px-6 py-3 rounded-lg shadow cursor-pointer hover:bg-violet-950"
          >
            Sign up
          </button>
        </div>
      </section>

      <PageADaySection />

      {/* Public Domain Books Section */}
      <section className="relative px-6 py-20 max-w-full">
        <h2 className="text-3xl font-bold mb-8 text-center">Public Domain Books</h2>
        <div className="relative">
          {/* Arrows */}
          <button
            onClick={() => scrollLeft(publicBooksRef)}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
          <button
            onClick={() => scrollRight(publicBooksRef)}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white/70 hover:bg-white shadow rounded-full"
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-600" />
          </button>

          {/* Scrollable Container */}
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="w-8 h-8 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : publicBooks.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <img src="/images/empty-bookshelf.svg" alt="No books" className="mx-auto h-40 mb-4" />
              <p className="text-lg font-medium">No public domain books available right now.</p>
              <p className="text-sm mt-2">Please check back later — we're adding more classics soon.</p>
            </div>
          ) : (
            <div
              ref={publicBooksRef}
              className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth gap-6 px-8"
              >
              {Array.from({ length: Math.ceil(publicBooks.length / 2) }).map((_, colIndex) => (
                <div
                  key={colIndex}
                  className="flex-shrink-0 flex flex-col gap-6 snap-start"
                  >
                  {publicBooks.slice(colIndex * 2, colIndex * 2 + 2).map((book, index) => (
                    <div
                      key={book.book_meta_unique_id}
                      className="flex flex-col items-center w-40 cursor-pointer group"
                      onClick={() => {
                        posthog.capture('clicked_public_book', {
                          book_id: book.book_meta_unique_id,
                          book_name: book.book_name
                        });
                        window.location.href =
                        book.is_authored === 1 || book.is_authored === true
                          ? `/read_authored/${book.book_meta_unique_id}`
                          : `/read_book/${book.book_meta_unique_id}`;
                      }}
                      >
                      <img
                        src={book.book_cover}
                        alt={book.book_name}
                        className="w-40 h-56 object-cover rounded-md shadow-md transition duration-300 transform group-hover:scale-105 group-hover:shadow-xl"
                      />
                      <p
                        className="mt-3 text-sm font-medium text-gray-700 text-center group-hover:text-violet-900 transition line-clamp-2 h-10"
                      >
                        {book.book_name}
                      </p>
                  </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {/* See All Books Link */}
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => handleNavigate('books')}
              className="inline-block text-violet-900 font-medium hover:underline transition"
            >
              See all books →
            </button>
          </div>
        </div>
      </section>

      <HomepageBlogsTicker />

    {/* Quotes Section */}
    <section id="quotes" className="relative px-6 py-20 max-w-full">
        <h2 className="text-3xl font-bold mb-8 text-center">Famous Quotes</h2>
        <div className="relative">
          {/* Scroll Buttons */}
          <button
            onClick={() => scrollLeft(quotesRef)}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 shadow rounded-full"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
          <button
            onClick={() => scrollRight(quotesRef)}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 shadow rounded-full"
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-600" />
          </button>

          {/* Scroll Container */}
          <div
            ref={quotesRef}
            className="flex space-x-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-8"
          >
            {[
              { quote: "A reader lives a thousand lives before he dies . . . The man who never reads lives only one.", author: "George R.R. Martin" },
              { quote:"There is more treasure in books than in all the pirate’s loot on Treasure Island.", author: "Walt Disney" },
              { quote: "Show me a family of readers, and I will show you the people who move the world.", author: "Napoleon Bonaparte" },
              { quote: "If you don’t like to read, you haven’t found the right book.", author: "J.K. Rowling" },
              { quote: "Today a reader, tomorrow a leader.", author: "Margaret Fuller" },
              { quote: "A book is a garden, an orchard, a storehouse, a party, a company by the way, a counselor, a multitude of counselors.", author: "Charles Baudelaire" },
            ].map(({ quote, author }, index) => (
              <div key={index} className="flex-shrink-0 w-80 snap-start bg-white p-6 rounded-lg shadow-md">
                <p className="text-lg italic">“{quote}”</p>
                <p className="mt-2 text-sm font-medium text-gray-500">— {author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="px-6 py-20 max-w-5xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-12">Reading Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-2">Enhanced Brain Function</h3>
            <p>Reading keeps the brain active and stimulated, potentially slowing cognitive decline and reducing the risk of conditions like Alzheimer's and dementia.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Improved Memory</h3>
            <p>Reading requires the brain to recall details and characters, strengthening memory.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Increased Vocabulary</h3>
            <p>Exposure to new words and concepts through reading expands vocabulary and improves communication skills.</p>
          </div>
        </div>
      </section>

      <NativeAffiliateBanner placement="home" />

      <section id="partners" className="px-6 py-20 bg-violet-50 text-center">
      <h2 className="text-3xl font-bold mb-6">Partner with Poppyruz</h2>
      <p className="max-w-3xl mx-auto text-lg text-gray-700 mb-12">
        Whether you're an individual looking to sell books, a bookstore aiming to reach more readers - we’d love to collaborate.
      </p>

     {/* <div className="space-y-6 text-lg text-gray-800">
        <p>📚 Want to sell your old or new books through our platform? <strong>Contact us.</strong></p>
        <p>🏪 Represent a bookshop and want to advertise your catalog? <strong>Reach out to us.</strong></p>
      </div> */}

      <div className="mt-10">
        <a
          href="/contact"
          className="inline-block bg-violet-900 text-white px-6 py-3 rounded-lg shadow hover:bg-violet-950 transition"
        >
          Get in Touch
        </a>
      </div>
    </section>

      <footer className="bg-gray-100 text-gray-700 px-6 py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-left">
          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Quick Links</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    handleNavigate('books');
                    posthog.capture('footer_clicked_public_books');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Public Domain Books
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/blogs');
                    posthog.capture('footer_clicked_blogs');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Blogs
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = `/contact`;
                    posthog.capture('footer_clicked_contact');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    setShowAboutModal(true);
                  }}
                  className="hover:underline text-left text-sm"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    window.location.href = `/premium`;
                    posthog.capture('footer_clicked_premium');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Pricing
                </button>
              </li>
            </ul>
          </div>
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Contact</h3>
            <p className="text-sm">📧 support@poppyruz.com</p>
            <p className="text-sm">📍 Tirunelveli, India</p>
          </div>
          {/* Feedback & Legal */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Support & Legal</h3>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  onClick={() => {
                    window.location.href = `/contact`;
                    posthog.capture('footer_clicked_feedback');
                  }}
                  className="hover:underline text-left text-sm"
                >
                  Give Feedback
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowReportModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Report Copyright Violation
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Terms & Conditions
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowPrivacyModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowRefundModal(true)}
                  className="hover:underline text-left text-sm"
                >
                  Cancellation & Refund Policy
                </button>
              </li>
            </ul>
          </div>
          {/* Social Media */}
          <div>
            <h3 className="font-semibold mb-2 text-lg">Follow Us</h3>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/zindha_madhar?igsh=MXZid3QzdGd4bnQ2Ng=="
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/instagram.svg" alt="Instagram" className="h-5 w-5" /></a>
              <a href="https://www.linkedin.com/in/mohamed-sindha-madhar-a9525822a"
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/linkedin.svg" alt="LinkedIn" className="h-5 w-5" /></a>
              <a href="https://www.youtube.com/@Poppyruz"
              target="_blank"
              rel="noopener noreferrer"
              ><img src="/icons/youtube.svg" alt="YouTube" className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center text-sm text-gray-400">
          © 2026 Poppyruz. All rights reserved.
        </div>
      </footer>
      <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
        />
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <AboutUsModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />  
      <CancellationRefundModal isOpen={showRefundModal} onClose={() => setShowRefundModal(false)} />  
    </div>
  );
}
